import { Vector4 } from 'three';
import { DepthClearValue } from '../CommonRenderTargets';
import { RenderBatch } from "../../scene/RenderBatch";
import { vectorToABGR } from "./uniforms/ObjectUniforms";
import { UberPipeline } from "./UberPipeline";
import { EdgePipeline } from "./EdgePipeline";
import { LinePipeline } from "../2d/LinePipeline";
import { CustomShaderPipeline } from "./CustomShaderPipeline";
import { ShaderMaterialWebGPU } from "./ShaderMaterialWebGPU";
import { MaterialUniforms, MaterialUniformFlags } from "./MaterialUniforms";
import { RenderBatchShim } from "../sceneToBatch";
import { IBL } from "./IBL";
import { RenderBatchUniforms } from "./uniforms/RenderBatchUniforms";
import { CameraUniforms } from "./CameraUniforms";
import { LineUniforms } from "../2d/LineUniforms";
import { FrameBindGroup } from "./FrameBindGroup";
import { Renderer } from "../Renderer";
import { USE_OUT_OF_CORE_TILE_MANAGER, USE_WBOIT, USE_OVERLAY_DEPTH_COPY } from "../../globals";
import { UsingMultiDraw, UsingDrawIndirect } from "../globals";
import { GPUSceneRenderer } from "../gpudrivenrender/GPUSceneRenderer";
import { StateTrackingEncoder } from "../StateTrackingEncoder";

/** @import { TimingManager } from '../TimingManager'; */
/** @import { MultiDrawBatch } from '../gpudrivenrender/MultiDrawBatch' */

// These settings are only used during pipeline creation. No actual material needed.
const edgeMaterialSettings = {
  depthTest: true,
  depthWrite: false,
  depthFunc: 'less-equal'
};

const edgeHighlightMaterialUnder = {
  depthTest: true,
  depthWrite: false,
  depthFunc: 'greater'
};

const edgeHighlightMaterialOver = {
  depthTest: true,
  depthWrite: false,
  depthFunc: 'less-equal'
};

// @todo DoubleSide is forced so that back faces (visible through transparency) are selectable. If specific models
// use single-sided transparent panels, this may cause unexpected selection through interior faces.
const idOnlyMaterialSettings = {
  depthFunc: 'less-equal',
  side: 2, // THREE.DoubleSide
  vertexColors: false,
  wideLines: false,
  __gpuUniformsMask: MaterialUniformFlags.NO_UNIFORMS,
  needsUpdate: false
};

const ghostMaterialSettings = {
  depthTest: true,
  depthWrite: true,
  depthFunc: 'less'
};

/**
 * @todo connect to WebGL values in https://jira.autodesk.com/browse/VIZX-1508
 * Using the same values as WebGL, edges still look more prominent in WebGPU. There could be a difference in how lines
 * are rasterized (see also https://www.w3.org/TR/webgpu/#line-rasterization).
 */

/** DPR-dependent edge opacity. High-DPI screens render sharper edges that benefit from higher opacity. */
export const EdgeOpacity = Object.freeze({
  Default: Object.freeze({ low: 0.60, high: 1.00 }),
  Ghosted: Object.freeze({ low: 0.15, high: 0.25 })
});

/** Shared edge color presets (RGBA in [0,1]). Used by Viewer3DImpl, MainPass, and RenderContextHelper. */
export const EdgeColor = Object.freeze({
  Dark: Object.freeze(new Vector4(0, 0, 0, EdgeOpacity.Default.low)),
  DarkGhosted: Object.freeze(new Vector4(0, 0, 0, EdgeOpacity.Ghosted.low)),
  LightGhosted: Object.freeze(new Vector4(0.75, 0.875, 1, EdgeOpacity.Ghosted.low)),
  Highlight: Object.freeze(new Vector4(1, 1, 1, 1.0)),
  HighlightUnder: Object.freeze(new Vector4(1, 1, 1, 0.5))
});

/**
 * @param {Renderer} renderer
 */
export class MainPass {
  /** @type {Renderer} */
  #renderer;
  /** @type {GPUDevice} */
  #device;

  /** @type {GPURenderPassDescriptor} */
  #mainPassDescriptorProg;
  /** @type {GPURenderPassDescriptor} */
  #clearPassDescriptor;
  /** @type {GPURenderPassDescriptor} */
  #clearDepthDescriptor;
  /** @type {GPURenderPassDescriptor} */
  #overlayPassDescriptorWithClear;
  /** @type {GPURenderPassDescriptor} */
  #overlayPassDescriptorNoClear;

  /** @type {GPURenderBundleEncoderDescriptor} */
  #renderBundleDescriptor;

  // Used for WBOIT (Weighted Blended Order-Independent Transparency)
  /** @type {GPURenderPassDescriptor} */
  #wboitPassDescriptor;
  /** @type {GPURenderPassDescriptor} */
  #wboitClearPassDescriptor;
  /** @type {GPURenderBundleEncoderDescriptor} */
  #wboitRenderBundleDescriptor;
  /** @type {GPUCommandEncoder|null} Used for transparent commands. */
  #wboitCmdEncoder = null;
  /** @type {StateTrackingEncoder|null} Used for transparent draws. */
  #wboitPassEncoder = null;

  // ID-only pass: records transparent geometry into a separate command encoder during WBOIT, using a
  // copy of the opaque-only depth buffer. Transparent fragments compete via depth test — closest wins.
  /** @type {GPURenderPassDescriptor} */
  #idOnlyPassDescriptor;
  /** @type {GPUCommandEncoder|null} */
  #idCmdEncoder = null;
  /** @type {StateTrackingEncoder|null} */
  #idPassEncoder = null;
  // When true, subsequent depth copies are skipped — ID depth accumulates transparent writes across
  // progressive frames. clearWboitTargets() guarantees one final copy via #pendingIdDepthCopy.
  #skipIdDepthCopy = false;
  #pendingIdDepthCopy = false;

  #renderBundle;
  // Whether the current RenderBatch will use render bundles. Only valid within a renderScenePart() call.
  #useRenderBundles;
  // If true, render bundles are always disabled.
  #disableRenderBundles = false;


  /** @type {IBL} */
  #iblUniforms;

  //TODO: Investigate using a ring buffer here in case uploading
  //object uniforms for each render batch using the same buffer is considered a bottleneck
  /** @type {RenderBatchUniforms} */
  #renderBatchUniforms;
  /** @type {MaterialUniforms} */
  #materialUniforms;
  /** @type {CameraUniforms} */
  #cameraUniforms;
  #lineUniforms;

  /** @type {FrameBindGroup} */
  #frameBindGroup;

  /** @type {UberPipeline} */
  #mainPipeline;
  /** @type {UberPipeline} */
  #overlayPipeline;
  /** @type {EdgePipeline} */
  #edgePipeline;

  /** @type {boolean} */
  #wboitBatch = false;

  /** @type {CustomShaderPipeline} */
  #customShaderPipeline;
  /** @type {LinePipeline} */
  #linePipeline;
  /** @type {LinePipeline} */
  #linePipelineOverlays; // For overlays, we need a separate pipeline, because the Pipelines must be created for different target setups.

  #edgeColorMainInt = vectorToABGR(EdgeColor.Dark);
  #edgeColorGhostedInt = vectorToABGR(EdgeColor.DarkGhosted);

  #bindGroupLayouts;

  #geometriesList = [];
  #renderIndicesList = [];

  // Reusable shim for wrapping THREE.Scenes so we don't need to recreate it multiple times per frame.
  #rBatchShim;

  // Temporarily used during a renderScenePart() call. Needed to make it accessible to renderCallback functions.
  #currentRenderBatch;

  // RenderBatch->Array of RenderBundles
  #renderBundles = new Map();

  /** @type {TimingManager|undefined} */
  #timingManager;

  /**
    * @type {THREE.Camera} Camera specified in beginScene() and expected in renderScenePart() calls.
    *
    * @note Why needed?
    *       - renderScenePart() assumes that camera uniforms are pre-configured for the main camera in beginScene().
    *       - This might not be true if overlays with own cameras are rendered between progressive frames.
    *      => To ensure that renderScenePart renders with the expected camera uniforms, we track which camera was used last and recover the correct camera uniforms if needed.
    */
  #sceneCamera = null;

  /** @type {THREE.Camera} Camera used for last cameraUniforms update (might be different when using overlays with custom cameras) */
  #lastAppliedCamera = null;

  /** @type {GPUCommandEncoder|null} Used for normal draw commands. */
  #commandEncoder = null;
  /** @type {GPUCommandEncoder|null} Used only for culling compute commands. */
  #cullCmdEncoder = null;
  /** @type {GPURenderPassDescriptor|null} Descriptor used by the current render pass. */
  #passDescriptor = null;
  /** @type {StateTrackingEncoder|null} The active render pass encoder. */
  #passEncoder = null;
  /**
   * @type {StateTrackingEncoder|null}
   * The encoder we're actively recording a render pass into. This could be
   * either the normal render pass encoder, or a render bundle encoder.
   */
  #encoder = null;

  /** @type {number} The number of geometries drawn from the current batch. */
  #count = 0;
  /** @type {number} Number of batches recorded into the current command encoder. */
  #batchesRecorded = 0;
  /** @type {number} How many batches we should record before submitting the command buffer. */
  #maxBatchesPerSubmit = 3;

  constructor(renderer) {
    this.#renderer = renderer;
    this.#iblUniforms = new IBL(renderer);
    this.#rBatchShim = new RenderBatchShim();
  }

  #createMainPass() {

    let rt = this.#renderer.getRenderTargets();
    let colorTargetView = rt.getColorTarget().createView();
    let depthTargetView = rt.getDepthTarget().createView();
    let normalsTargetView = rt.getNormalsTarget().createView();
    let viewDepthTargetView = rt.getViewDepthTarget().createView();
    let overlayTargetView = rt.getOverlayTarget().createView();
    let overlayDepthTargetView = USE_OVERLAY_DEPTH_COPY ? rt.getOverlayDepthTarget().createView() : depthTargetView;

    this.#clearPassDescriptor = {
      colorAttachments: [
      {
        view: normalsTargetView,
        clearValue: { r: 0, g: 0, b: 0, a: 0 },
        loadOp: 'clear',
        storeOp: 'store'
      },
      {
        view: viewDepthTargetView,
        clearValue: { r: 0, g: 0, b: 0, a: 0 },
        loadOp: 'clear',
        storeOp: 'store'
      }],

      depthStencilAttachment: {
        view: depthTargetView,
        depthClearValue: DepthClearValue,
        depthLoadOp: 'clear',
        depthStoreOp: 'store'
      }
    };

    this.#clearDepthDescriptor = {
      colorAttachments: [],
      depthStencilAttachment: {
        view: depthTargetView,
        depthClearValue: DepthClearValue,
        depthLoadOp: 'clear',
        depthStoreOp: 'store'
      }
    };

    this.#mainPassDescriptorProg = {
      colorAttachments: [
      {
        view: colorTargetView,
        loadOp: 'load',
        storeOp: 'store'
      },
      {
        view: normalsTargetView,
        loadOp: 'load',
        storeOp: 'store'
      },
      {
        view: viewDepthTargetView,
        loadOp: 'load',
        storeOp: 'store'
      }],

      depthStencilAttachment: {
        view: depthTargetView,
        depthLoadOp: 'load',
        depthStoreOp: 'store'
      }
    };

    const sampleCount = rt.getSampleCount();
    this.#renderBundleDescriptor = {
      colorFormats: [
      rt.getColorTarget().format,
      rt.getNormalsTarget().format,
      rt.getViewDepthTarget().format],

      depthStencilFormat: rt.getDepthTarget().format,
      sampleCount
    };

    for (let i = 0; i < rt.getNumIdTargets(); i++) {

      let attachment = {
        view: rt.getIdTarget(i).createView(),
        clearValue: { r: 255, g: 255, b: 255, a: 255 }, // match WebGLRenderer, 0xffffffff
        loadOp: 'clear',
        storeOp: 'store'
      };

      let attachmentProg = {
        view: attachment.view,
        loadOp: 'load',
        storeOp: 'store'
      };

      let format = rt.getIdTarget(i).format;

      this.#clearPassDescriptor.colorAttachments.push(attachment);
      this.#mainPassDescriptorProg.colorAttachments.push(attachmentProg);
      this.#renderBundleDescriptor.colorFormats.push(format);
    }

    this.#overlayPassDescriptorWithClear = {
      colorAttachments: [
      {
        view: overlayTargetView,
        clearValue: { r: 0, g: 0, b: 0, a: 0 },
        loadOp: 'clear',
        storeOp: 'store'
      }],

      // Only attach a separate overlay depth when USE_OVERLAY_DEPTH_COPY is enabled.
      // Without it, overlays share the main depth buffer and must not clear it.
      ...(USE_OVERLAY_DEPTH_COPY ? {
        depthStencilAttachment: {
          view: overlayDepthTargetView,
          depthLoadOp: 'clear',
          depthClearValue: DepthClearValue,
          depthStoreOp: 'store'
        }
      } : {})
    };

    // The overlay pipeline does not write to the normals, viewDepth or id
    // targets, but they are needed in this descriptor to be compatible with
    // the EdgePipeline.
    this.#overlayPassDescriptorNoClear = {
      colorAttachments: [
      {
        view: overlayTargetView,
        loadOp: 'load',
        storeOp: 'store'
      },
      {
        view: normalsTargetView,
        loadOp: 'load',
        storeOp: 'store'
      },
      {
        view: viewDepthTargetView,
        loadOp: 'load',
        storeOp: 'store'
      }],

      depthStencilAttachment: {
        view: overlayDepthTargetView,
        depthLoadOp: 'load',
        depthStoreOp: 'store'
      }
    };

    for (let i = 0; i < rt.getNumIdTargets(); i++) {
      this.#overlayPassDescriptorNoClear.colorAttachments.push({
        view: rt.getIdTarget(i).createView(),
        loadOp: 'load',
        storeOp: 'store'
      });
    }

    // WBOIT descriptors (only created if WBOIT is enabled)
    if (USE_WBOIT) {
      let wboitAccumView = rt.getWboitAccumView();
      let wboitRevealageView = rt.getWboitRevealageView();

      this.#wboitPassDescriptor = {
        colorAttachments: [
        {
          view: wboitAccumView,
          loadOp: 'load',
          storeOp: 'store'
        },
        {
          view: wboitRevealageView,
          loadOp: 'load',
          storeOp: 'store'
        }],

        depthStencilAttachment: {
          view: depthTargetView,
          depthLoadOp: 'load',
          depthStoreOp: 'store'
        }
      };
      this.#wboitClearPassDescriptor = {
        colorAttachments: [
        {
          view: wboitAccumView,
          clearValue: { r: 0, g: 0, b: 0, a: 0 }, // Clear accumulation to 0
          loadOp: 'clear',
          storeOp: 'store'
        },
        {
          view: wboitRevealageView,
          clearValue: { r: 1, g: 1, b: 1, a: 1 }, // Clear revealage to 1
          loadOp: 'clear',
          storeOp: 'store'
        }]

      };

      this.#wboitRenderBundleDescriptor = {
        colorFormats: rt.getWboitFormats(),
        depthStencilFormat: rt.getDepthTarget().format,
        sampleCount
      };

      this.#idOnlyPassDescriptor = {
        colorAttachments: [],
        depthStencilAttachment: {
          view: rt.getIdOnlyDepthTarget().createView(),
          depthLoadOp: 'load',
          depthStoreOp: 'store'
        }
      };

      for (let i = 0; i < rt.getNumIdTargets(); i++) {
        this.#idOnlyPassDescriptor.colorAttachments.push({
          view: rt.getIdTarget(i).createView(),
          loadOp: 'load',
          storeOp: 'store'
        });
      }
    }

  }

  init(renderBatchUniforms) {

    this.#device = this.#renderer.getDevice();

    this.#iblUniforms.init();
    this.#cameraUniforms = new CameraUniforms(this.#device);
    this.#frameBindGroup = new FrameBindGroup(
      this.#device, this.#cameraUniforms, this.#iblUniforms);

    //TODO: Investigate using a ring buffer here in case uploading
    //object uniforms for each render batch using the same buffer is considered a bottleneck
    this.#renderBatchUniforms = renderBatchUniforms;
    this.#geometriesList.length = this.#renderBatchUniforms.MAX_BATCH;
    this.#renderIndicesList.length = this.#renderBatchUniforms.MAX_BATCH;

    this.#materialUniforms = new MaterialUniforms(this.#device, undefined, this.#renderer.getPlaceholderTexture());

    this.#lineUniforms = new LineUniforms(this.#device);

    const defaultMaterialBindGroup = this.#materialUniforms.getBindGroup();
    this.#mainPipeline = new UberPipeline(this.#renderer, defaultMaterialBindGroup);
    this.#edgePipeline = new EdgePipeline(this.#renderer);
    this.#linePipeline = new LinePipeline(this.#renderer);
    this.#linePipelineOverlays = new LinePipeline(this.#renderer);
    this.#customShaderPipeline = new CustomShaderPipeline(this.#renderer);

    //The overlay pipeline uses a different set of render targets,
    //so we maintain a separate instance of the UberPipeline for it (since targets list is not taken into account
    //in pipeline cache keys)
    this.#overlayPipeline = new UberPipeline(this.#renderer, defaultMaterialBindGroup);

    this.#bindGroupLayouts = [
    this.#frameBindGroup.getLayout(),
    this.#renderBatchUniforms.getLayout(),
    this.#materialUniforms.getLayout()];


    const lineLayouts = [
    this.#frameBindGroup.getLayout(),
    this.#renderBatchUniforms.getLayout(),
    this.#lineUniforms.getLayout()];

    this.#linePipeline.setLayouts(...lineLayouts);
    this.#linePipelineOverlays.setLayouts(...lineLayouts);

    this.#timingManager = this.#renderer.getTimingManager();

  }

  resize(w, h) {
    this.#createMainPass(w, h);
    this.#lineUniforms.setTargetSize(w, h);
    this.#lineUniforms.upload();
  }

  /**
   * Submits a pass for the given descriptor which clears its attachments (usually via loadOp: clear).
   * @Note loadOp: clear ignores the viewport that is set. Thus rendering to different viewports
   *       with a clear in between currently would not work.
   *
   * @param {GPURenderPassDescriptor} descriptor
   */
  #clearTarget(descriptor) {
    let commandEncoder = this.#device.createCommandEncoder({ label: 'clear pass encoder' });
    const passEncoder = commandEncoder.beginRenderPass(descriptor);
    // false-positive issue detected by Chorus, see https://semgrep.dev/docs/ignoring-files-folders-code
    // nosemgrep
    passEncoder.label = 'clear pass';
    // The next line would have no effect as loadOp: clear is not affected by the viewport.
    // this.#renderer.passViewport(passEncoder);
    passEncoder.end();
    this.#device.queue.submit([commandEncoder.finish()]);
  }

  clearMainTargets() {
    this.#clearTarget(this.#clearPassDescriptor);
    if (USE_WBOIT) {
      this.#clearTarget(this.#wboitClearPassDescriptor);
      this.#skipIdDepthCopy = false;
    }
  }

  setSkipIdDepthCopy(skip) {
    this.#skipIdDepthCopy = skip;
  }

  clearWboitTargets() {
    // If we're clearing these targets, then any in-progress transparent
    // draws are outdated and should be dropped.
    this.#wboitPassEncoder = null;
    this.#wboitCmdEncoder = null;
    this.#idPassEncoder = null;
    this.#idCmdEncoder = null;
    this.#pendingIdDepthCopy = true;
    this.#clearTarget(this.#wboitClearPassDescriptor);
  }

  clearOverlayTargets() {
    this.#clearTarget(this.#overlayPassDescriptorWithClear);
  }

  clearMainDepth() {
    this.#clearTarget(this.#clearDepthDescriptor);
  }

  copyDepthToOverlay() {
    const rt = this.#renderer.getRenderTargets();
    const src = rt.getDepthTarget();
    const dst = rt.getOverlayDepthTarget();
    const [w, h] = rt.getTargetSize();

    const commandEncoder = this.#device.createCommandEncoder({ label: 'copy depth to overlay encoder' });
    commandEncoder.copyTextureToTexture(
      { texture: src, aspect: 'depth-only' },
      { texture: dst, aspect: 'depth-only' },
      [w, h]
    );
    this.#device.queue.submit([commandEncoder.finish()]);
  }

  setGhostingBrightness(darker) {

    // Not implemented.
  }
  /** @param {number} value Either 0.0 (no swap) or 1.0 (swap). */
  setSwapBlackAndWhite(value) {
    this.#lineUniforms.setSwapBlackAndWhite(value);
    this.#lineUniforms.upload();
  }

  /** @param {{ x: number, y: number, z: number, w: number }} [colorAsVec4] - RGBA in [0,1]. Otherwise defaults. */
  setEdgeColor(colorAsVec4 = EdgeColor.Dark) {
    this.#edgeColorMainInt = vectorToABGR(colorAsVec4);
  }

  /** @param {{ x: number, y: number, z: number, w: number }} [colorAsVec4] - RGBA in [0,1]. Otherwise defaults. */
  setEdgeColorGhosted(colorAsVec4 = EdgeColor.DarkGhosted) {
    this.#edgeColorGhostedInt = vectorToABGR(colorAsVec4);
  }

  static GHOSTED_BUNDLE_INDEX = 2;

  /**
   * Set whether edges in ghosted mode should use dithering. Forwards to EdgePipeline and invalidates render bundles.
   * @param {boolean} [dithered=false]
   */
  setDitheredGhosting(dithered = false) {
    if (!this.#edgePipeline.setDitheredGhosting(dithered)) return; // no invalidation needed

    // clear cached ghosted pass bundles so they are re-recorded
    for (const bundles of this.#renderBundles.values()) {
      bundles[MainPass.GHOSTED_BUNDLE_INDEX] = null;
    }
  }

  updatePixelScale(pixelsPerUnit, camera) {
    if (!this.#lineUniforms) return;
    this.#lineUniforms.updatePixelScale(pixelsPerUnit, camera);
    this.#lineUniforms.upload();
  }

  setLineStyleBuffer(buffer, width) {
    if (!this.#lineUniforms) return;
    this.#lineUniforms.setLineStyleBuffer(buffer, width);
    this.#lineUniforms.upload();
  }

  resetPipelines() {
    // If MainPass isn't initialized yet, there is nothing to do
    if (!this.#renderBatchUniforms) return;

    // Refresh bind group layouts when extensions changed them
    const newLayout = this.#renderBatchUniforms.getLayout();
    if (this.#bindGroupLayouts[1] !== newLayout) {
      this.#bindGroupLayouts[1] = newLayout;
      const lineLayouts = [
      this.#bindGroupLayouts[0],
      newLayout,
      this.#lineUniforms.getLayout()];

      this.#linePipeline?.setLayouts(...lineLayouts);
      this.#linePipelineOverlays?.setLayouts(...lineLayouts);
    }

    this.#mainPipeline?.resetPipelines();
    this.#overlayPipeline?.resetPipelines();
    this.#edgePipeline?.resetPipelines();
    this.#customShaderPipeline?.resetPipelines();
    this.#linePipeline?.resetPipelines();
    this.#linePipelineOverlays?.resetPipelines();
  }

  getIBL() {return this.#iblUniforms;}

  disableRenderBundles() {
    this.#disableRenderBundles = true;
  }

  viewportChanged() {
    // Update any ongoing pass encoders.
    if (this.#passEncoder) {
      this.#renderer.passViewport(this.#passEncoder);
    }
  }

  /**
   * Ensure that the current render pass encoder is created with the provided
   * passDescriptor. If needed, the current encoder will be ended and a new
   * render pass encoder will be created.
   * @param {string} label
   * @param {GPURenderPassDescriptor} passDescriptor
   */
  #ensureRenderPass(label, passDescriptor) {
    if (!this.#passEncoder || this.#passDescriptor !== passDescriptor) {
      this.#endRenderPass();

      passDescriptor.label = label;
      if (this.#timingManager) {
        this.#timingManager.newPassQuery(passDescriptor);
      }
      const rawPassEncoder = this.#commandEncoder.beginRenderPass(passDescriptor);
      this.#passEncoder = new StateTrackingEncoder(rawPassEncoder);
      this.#passDescriptor = passDescriptor;
      this.#renderer.passViewport(this.#passEncoder);
    }
  }

  #endRenderPass() {
    if (!this.#passEncoder) {
      return;
    }
    this.#passEncoder.end();
    this.#passEncoder = null;
    this.#passDescriptor = null;
  }

  /**
   * Set this encoder's pipeline state for main pass rendering.
   * @param {StateTrackingEncoder} encoder
   * @param {RenderBatch|undefined} batch
   */
  #setPipelineState(encoder, batch) {
    // TODO: The frameBindGroup and materialUniforms bindgroup remain
    // constant regardless of the batch. It would be better if these were
    // both moved to set 0, and set only once at the beginning of the render
    // pass. Only the renderBatchUniforms bindgroup needs to be re-set when
    // we switch render batches.

    encoder.setBindGroupIfNeeded(0, this.#frameBindGroup.getBindGroup());
    // If batch is undefined, this will use the bindgroup for dynamic uniforms.
    encoder.setBindGroupIfNeeded(1, this.#renderBatchUniforms.getBindGroup(batch));
    encoder.setBindGroupIfNeeded(2, this.#materialUniforms.getBindGroup());

    this.#currentRenderBatch = batch;
  }

  /**
   * @param {string} label
   * @param {GPURenderPassDescriptor} passDescriptor
   * @param {LinePipeline} pipeline
   */
  #beginRenderPass2D(label, passDescriptor, pipeline) {
    this.#ensureRenderPass(label, passDescriptor);
    pipeline.setBindGroups(
      this.#passEncoder,
      this.#frameBindGroup.getBindGroup(),
      this.#renderBatchUniforms.getBindGroup(-1),
      this.#lineUniforms.getBindGroup()
    );
  }

  /**
   * Writes dynamic uniforms to the queue and triggers a submit.
   * Only call this when flushing dynamic uniforms is needed.
   * @param {number} count
   */
  #flushObjects(count) {
    this.#renderBatchUniforms.writeToQueue(count);
    this.submitCommands();
  }

  /** Submit any pending command buffers to the GPUQueue. */
  submitCommands() {
    this.#renderer.getVB().flushWrites();
    let cmds = this.#finishCommands();
    if (cmds.length) {
      this.#device.queue.submit(cmds);
    }
  }

  /** Submit any pending transparent command buffers to the GPUQueue. */
  submitWboitCommands() {
    this.#renderer.getVB().flushWrites();

    const cmdBufs = [];

    if (this.#wboitCmdEncoder) {
      this.#wboitPassEncoder.end();
      this.#wboitPassEncoder = null;
      cmdBufs.push(this.#wboitCmdEncoder.finish());
      this.#wboitCmdEncoder = null;
    }

    // The ID pass runs after WBOIT and uses a copy of the opaque-only depth buffer. Transparent
    // fragments compete via standard depth test — closest wins — producing correct IDs every frame.
    if (this.#idCmdEncoder) {
      this.#idPassEncoder.end();
      this.#idPassEncoder = null;
      cmdBufs.push(this.#idCmdEncoder.finish());
      this.#idCmdEncoder = null;
    }

    if (cmdBufs.length) {
      this.#device.queue.submit(cmdBufs);
    }
  }


  /**
   * @returns {GPUCommandBuffer[]}
   */
  #finishCommands() {
    let cmds = [];
    // Culling should be submitted before draws.
    if (this.#cullCmdEncoder) {
      cmds.push(this.#cullCmdEncoder.finish());
      this.#cullCmdEncoder = null;
    }
    if (this.#commandEncoder) {
      this.#endRenderPass();
      cmds.push(this.#commandEncoder.finish());
      this.#commandEncoder = null;
      this.#batchesRecorded = 0;
    }
    return cmds;
  }

  /**
   * To be called after recording a RenderBatch's draws into the current
   * command encoder. This may submit the current command encoder if a certain
   * number of render batches have been recorded.
   */
  #onBatchRecorded() {
    // If there's no active command encoder then we may have already flushed
    // it. Nothing to do here.
    if (!this.#commandEncoder) {
      return;
    }

    this.#batchesRecorded++;
    if (this.#batchesRecorded >= this.#maxBatchesPerSubmit) {
      this.submitCommands();
    }
  }

  beginScene(camera, lights) {
    this.applyCamera(camera);
    this.#sceneCamera = camera;

    if (this.#iblUniforms.update(lights)) {
      this.#frameBindGroup.updateBindGroup();
      this.#renderer.invalidateRenderBundles();
    }
  }

  applyCamera(camera) {
    this.#lastAppliedCamera = camera;
    this.#cameraUniforms.update(camera);
  }

  /**
   * Slower variant for RenderBatches that don't have pre-uploaded uniforms
   * @param {number} fragId // all other params are unused in this variant
   * @returns {boolean} true indicates that the current RenderBatch should be interrupted and resumed next frame
   */
  #renderBatchCallbackSlow(_geometry, _material, _fragIndex, fragId) {
    const frags = this.#currentRenderBatch.frags;
    const mesh = frags.getVizmesh(fragId);
    return this.#threeSceneCallback(mesh);
  }

  /**
   * Slower variant for RenderBatches that don't have pre-uploaded uniforms
   * @param {number} fragId // all other params are unused in this variant
   * @returns {boolean} true indicates that the current RenderBatch should be interrupted and resumed next frame
   */
  #renderBatchCallbackSlowGhosted(_geometry, _material, _fragIndex, fragId) {
    const frags = this.#currentRenderBatch.frags;
    const mesh = frags.getVizmesh(fragId);
    return this.#threeSceneCallbackGhosted(mesh);
  }

  /**
   * Render callbacks (passed to forEachWebGPU) for the main pass.
   * 2d and overlay passes still have custom callbacks defined inline.
   * @param {BufferGeometry} geometry
   * @param {THREE.Material} material
   * @param {number} fragIndex - Order index of the fragment within renderBatch.getIndices().
   * @returns {boolean} true indicates that the current RenderBatch should be interrupted and resumed next frame
   */
  #renderBatchCallback(geometry, material, fragIndex, _fragId) {
    geometry = this.#renderer.initGeometry(geometry);
    if (!geometry) {
      return true;
    }

    // TODO: Recording these is only required for the edge pass, so we could skip it when edges are not enabled.
    const renderIndex = this.#renderBatchUniforms.getRenderIndex(fragIndex);
    this.#geometriesList[this.#count] = geometry;
    this.#renderIndicesList[this.#count] = renderIndex;

    const pipeline = material.needsCustomPipeline ? this.#customShaderPipeline : this.#mainPipeline;

    //TODO: Two pass transparency (if we want that) needs to draw with flipped culling first
    //then a second time with regular culling

    //It looks like we set the uniforms after the draw call here, but remember
    //that all these calls are just queuing commands that get issued when we
    //flush the command encoder, nothing is actually getting drawn yet.
    const materialTextureMask = pipeline.drawOne(
      this.#encoder, renderIndex, geometry, material, this.#wboitBatch);

    this.#renderBatchUniforms.initMaterialUpdateHook(material, materialTextureMask);

    // If material.needsUpdate was true, pipeline.drawOne() will always do the required updates.
    // So, we can (and must) safely reset it to false here.
    material.needsUpdate = false;

    this.#count++;

    return false;
  }

  /**
   * Standard render callback for ghosted geometry.
   * @param {BufferGeometry} geometry
   * @param {number} fragIndex - Order index of the fragment within renderBatch.getIndices().
   * @returns {boolean} true indicates that the current RenderBatch should be interrupted and resumed next frame
   */
  #renderBatchCallbackGhosted(geometry, _material, fragIndex, _fragId) {
    geometry = this.#renderer.initGeometry(geometry);
    if (!geometry) {
      return true;
    }

    const renderIndex = this.#renderBatchUniforms.getRenderIndex(fragIndex);
    this.#edgePipeline.drawOneGhosted(this.#encoder, renderIndex, geometry, ghostMaterialSettings);
    return false;
  }

  /**
   * Used for THREE.Scenes (used for Overlays), but also  as for RenderBatches that require dynamic uniform uploads.
   * @param {THREE.Mesh} mesh
   * @returns {boolean} true indicates that the current RenderBatch should be interrupted and resumed next frame
   */
  #threeSceneCallback(mesh) {
    const material = mesh.material;
    const geometry = this.#renderer.initGeometry(mesh.geometry);
    if (!geometry) {
      return true;
    }

    this.#geometriesList[this.#count] = geometry;
    this.#renderIndicesList[this.#count] = this.#count;

    if (material instanceof THREE.ShaderMaterial || material instanceof ShaderMaterialWebGPU) {
      this.#renderBatchUniforms.setOneObjectData(mesh, this.#count);
      this.#customShaderPipeline.drawOne(this.#encoder, this.#count, geometry, material);
      this.#count++;
      return false;
    }

    //TODO: Two pass transparency (if we want that) needs to draw with flipped culling first
    //then a second time with regular culling

    //It looks like we set the uniforms after the draw call here, but remember
    //that all these calls are just queuing commands that get issued when we
    //flush the command encoder, nothing is actually getting drawn yet.
    const materialTextureMask = this.#mainPipeline.drawOne(
      this.#encoder, this.#count, geometry, material, this.#wboitBatch);

    const numInstances = geometry.numInstances;
    if (numInstances !== undefined) {
      this.#renderBatchUniforms.setObjectDataFromInstanceBuffer(mesh, this.#count);
      this.#count += numInstances;
    } else {
      this.#renderBatchUniforms.setOneObjectData(mesh, this.#count);
      this.#count++;
    }

    this.#renderBatchUniforms.setOneMaterialData(material, materialTextureMask);

    // If material.needsUpdate was true, pipeline.drawOne() will always do the required updates.
    // So, we can (and must) safely reset it to false here.
    material.needsUpdate = false;
    return false;
  }

  /**
   * Used for ghosting of THREE.Scenes or RenderBatches that require dynamic uniform uploads.
   * @param {THREE.Mesh} mesh
   * @returns {boolean} true indicates that the current RenderBatch should be interrupted and resumed next frame
   */
  #threeSceneCallbackGhosted(mesh) {
    const geometry = this.#renderer.initGeometry(mesh.geometry);
    if (!geometry) {
      return true;
    }

    this.#renderBatchUniforms.setOneObjectData(mesh, this.#count);

    this.#edgePipeline.drawOneGhosted(this.#encoder, this.#count, geometry, ghostMaterialSettings);
    this.#count++;
    return false;
  }

  /**
   * @param {GPURenderBundleEncoderDescriptor} descriptor
   * @returns {GPURenderBundleEncoder}
   */
  #createRenderBundle(descriptor) {
    const bundleEncoder = this.#device.createRenderBundleEncoder(descriptor);
    return new StateTrackingEncoder(bundleEncoder);
  }

  #finishRenderBundle(rBatch, index, passEncoder, renderBundle) {
    if (renderBundle) {
      let bundle = renderBundle;
      if (renderBundle.isBundleEncoder) {
        bundle = renderBundle.finish();
        this.setRenderBundle(rBatch, index, bundle);
      }
      passEncoder.executeBundles([bundle]);
    }
  }

  renderScenePart(scene, showEdges, renderToOverlay = false) {

    // Make sure our camera uniforms are still set according to current scene camera. This makes sure that the camera uniforms are
    // not affected by overlay custom camera that might have been applied between progressive frames.
    // Note that we don't need to handle actual changes of the camera values, because we always update in beginScene().
    if (this.#sceneCamera !== this.#lastAppliedCamera) {
      this.applyCamera(this.#sceneCamera);
    }

    let rt = this.#renderer.getRenderTargets();
    // TODO: We should avoid marking id targets dirty in the case that we
    // don't actually draw anything for this scene. This can occur for
    // example from RenderCommandSystem.cmdSceneAfterRender() with the
    // `viewer.sceneAfter` scene, which is often empty.
    rt.setIdTargetsDirty();

    let rBatch, modelId, callback, callbackGhosted;
    this.#useRenderBundles = false;
    let renderBundle, renderBundleEdges, renderBundleGhosted;

    // Optimized scenes are not immediately committed to GPU per scene part, but rather collected in
    // command groups and submitted later (see Renderer.flushCommandQueue). However, only works
    // if the RenderBatch uniforms have own GPU memory per batch.
    // If multiple RenderBatches or scenes reuse the same dynamic uniform buffer, we have to commit
    // them immediately (needsFlush=true) - otherwise, all would be rendered with the Uniforms of the last processed batch.
    let needsFlush;

    if (!(scene instanceof RenderBatch)) {
      this.#rBatchShim.setFromScene(scene, this.#cameraUniforms.getViewProjectionMatrix());
      rBatch = this.#rBatchShim;

      modelId = -1;
      needsFlush = true;
      callback = this.#threeSceneCallback.bind(this);
      callbackGhosted = this.#threeSceneCallbackGhosted.bind(this);
    } else {
      rBatch = scene;
      modelId = rBatch.frags.modelId;
      needsFlush = false;
      callback = this.#renderBatchCallback.bind(this);
      callbackGhosted = this.#renderBatchCallbackGhosted.bind(this);
      this.#renderBatchUniforms.resetUpdateHeuristic(modelId);

      // Why not for 2D?:
      //  - Wouldn't work: objectUniforms.updateBatch() is only implemented for 3D.
      //  - Isn't needed:  Unlike 3D, 2D uniforms are currently updated dynamically per frame anyway (see #renderScenePart2D).
      const is3d = !rBatch.is2d();
      if (is3d) {
        // When using OutOfCoreTIleManager, we don't want to upload all uniforms at once immediately and cannot preallocate
        // own memory for every RenderBatch.
        // Instead, we use dynamic on-the-fly uploads for all non-optimized RenderBatches.
        // This is slower, but necessary to avoid sneaking around the GPU memory and frame-budget control of OutOfCoreTileManager.
        //
        // Dynamic upload of RenderBatch uniforms is required if...
        //  1. using outOfCore (otherwise, we always upload everything) and
        //  2. The tile is currently not fully optimized. (Optimized tiles can be identified via .isComplete)
        //  3. FragmentList is using THREE.Meshes (currently only used for model-builder models to deal with dynamic size)
        const needsDynamicUniforms = USE_OUT_OF_CORE_TILE_MANAGER && !rBatch.isComplete || rBatch.frags.useThreeMesh;
        if (needsDynamicUniforms) {
          // Dynamic uniforms reuse the same buffer and must be uploaded on every render
          needsFlush = true;
          callback = this.#renderBatchCallbackSlow.bind(this);
          callbackGhosted = this.#renderBatchCallbackSlowGhosted.bind(this);
        } else {
          // RenderBatch with own GPU uniform memory only need updates on changes
          if (rBatch.uniformsNeedUpdate) {
            this.#renderBatchUniforms.updateBatch(rBatch);
            rBatch.uniformsNeedUpdate = false;
          }
        }

        this.#renderer.clear3dModelVisibilityDirty(modelId);
      }

      // We don't use render bundles for overlays or WBOIT batches. WBOIT batches need the callback
      // to run every frame so that #geometriesList is populated for the ID-only replay pass.
      // @todo Consider caching geometry lists alongside render bundles to re-enable bundling here.
      if (!this.#disableRenderBundles && !renderToOverlay && !(USE_WBOIT && rBatch.sortObjects)) {
        this.#useRenderBundles = rBatch.useRenderBundles;
      }

      if (this.#useRenderBundles) {
        renderBundle = this.getRenderBundle(rBatch, 0);
        renderBundleEdges = this.getRenderBundle(rBatch, 1);
        if (showEdges && renderBundle && !renderBundleEdges) {
          // The color render bundle has been recorded, but there's no edge render bundle yet
          // (edges might just have been enabled at runtime). We discard / re-record the existing color
          // render bundle, because we need to create the list of geometries for the edge pass anyway.
          renderBundle = null;
        }
        if (!showEdges && renderBundleEdges) {
          // A render bundle for edges has been recorded before, but edges are now disabled.
          // We discard the edge render bundle, to avoid using a stale one if edges are re-enabled later.
          this.setRenderBundle(rBatch, 1, null);
          renderBundleEdges = null;
        }
        renderBundleGhosted = this.getRenderBundle(rBatch, MainPass.GHOSTED_BUNDLE_INDEX);
      }
    }

    if (rBatch.is2d()) {
      this.#renderScenePart2D(rBatch);
      return;
    }

    // Select targets and descriptors based on WBOIT mode
    // Only use WBOIT for scenes that need order-independent transparency (sortObjects = true)
    let targets;
    let edgeTargets;
    let renderBundleDescriptor;
    if (!renderToOverlay && USE_WBOIT && scene.sortObjects) {
      this.#wboitBatch = true;
      edgeTargets = targets = rt.getWboitTargets();
      renderBundleDescriptor = this.#wboitRenderBundleDescriptor;

      if (!this.#wboitCmdEncoder) {
        this.#wboitCmdEncoder = this.#device.createCommandEncoder(
          { label: 'wboit renderScenePart encoder' });

        const rawPassEncoder =
        this.#wboitCmdEncoder.beginRenderPass(this.#wboitPassDescriptor);
        this.#wboitPassEncoder = new StateTrackingEncoder(rawPassEncoder);
        this.#renderer.passViewport(this.#wboitPassEncoder);

        this.#idCmdEncoder = this.#device.createCommandEncoder(
          { label: 'transparent id renderScenePart encoder' });

        // Copy opaque depth into the ID depth target. Skipped during progressive OIT (after opaques done) so
        // transparent depth writes accumulate across intermediate frames. #pendingIdDepthCopy guarantees one
        // final copy after clearWboitTargets even when skipping is enabled.
        const doIdDepthCopy = this.#pendingIdDepthCopy || !this.#skipIdDepthCopy;
        if (doIdDepthCopy) {
          this.#pendingIdDepthCopy = false;
          const depthTexture = rt.getDepthTarget();
          this.#idCmdEncoder.copyTextureToTexture(
            { texture: depthTexture },
            { texture: rt.getIdOnlyDepthTarget() },
            [depthTexture.width, depthTexture.height]
          );
        }
        const rawIdPassEncoder =
        this.#idCmdEncoder.beginRenderPass(this.#idOnlyPassDescriptor);
        this.#idPassEncoder = new StateTrackingEncoder(rawIdPassEncoder);
        this.#renderer.passViewport(this.#idPassEncoder);
      }
    } else {
      this.#wboitBatch = false;
      targets = renderToOverlay ? rt.getOverlayTargetsList() : rt.getTargetsListMainPass();
      edgeTargets = renderToOverlay ? rt.getOverlayTargetsList() : rt.getTargetsListEdgePass();
      renderBundleDescriptor = this.#renderBundleDescriptor;
    }


    this.#renderBatchUniforms.setDoNotCutOverride(false);

    let mdBatch = GPUSceneRenderer.maybeGetMultiDrawBatch(rBatch);

    let startIndex = 0;
    do {
      this.#count = 0;

      if (!this.#commandEncoder) {
        this.#commandEncoder = this.#device.createCommandEncoder(
          { label: 'renderScenePart encoder' });
      }

      if (renderToOverlay) {
        this.#ensureRenderPass(
          'overlay pass', this.#overlayPassDescriptorNoClear);
      } else {
        this.#ensureRenderPass(
          // false-positive issue detected by Chorus, see https://semgrep.dev/docs/ignoring-files-folders-code
          // nosemgrep
          'main forward pass', this.#mainPassDescriptorProg);
      }

      let passEncoder;
      if (this.#wboitBatch) {
        passEncoder = this.#wboitPassEncoder;
      } else {
        passEncoder = this.#passEncoder;
      }

      if (mdBatch && mdBatch.enableCulling) {
        if (!this.#cullCmdEncoder) {
          this.#cullCmdEncoder = this.#device.createCommandEncoder(
            { label: 'cull command encoder' });
        }
        // TODO: It would probably be better to use the same
        // GPUComputePassEncoder for all cull passes (since they're
        // already within the same command encoder executed before all
        // the draws in the draw command encoder. We could keep one open
        // the same way we do with #passEncoder.
        this.#renderer.getGPUCullingEngine().cull(this.#cullCmdEncoder, mdBatch);
      }

      // Upload extension-specific per-batch data (before render bundle logic,
      // so that buffer contents are current even when replaying a cached bundle).
      this.#renderBatchUniforms.uploadExtensionsForBatch(rBatch);

      if (!scene.edgesOnly) {

        this.#renderBatchUniforms.setEdgeColorInt(this.#edgeColorMainInt);

        if (this.#useRenderBundles) {
          if (!renderBundle) {
            renderBundle = this.#createRenderBundle(renderBundleDescriptor);
          }
        }

        if (renderBundle && renderBundle.isBundleEncoder) {
          this.#encoder = renderBundle;
        } else {
          this.#encoder = passEncoder;
        }

        this.#mainPipeline.reset(this.#bindGroupLayouts, targets);
        this.#customShaderPipeline.reset(this.#bindGroupLayouts, targets);

        let endIndex = 0;
        if (!this.#useRenderBundles || renderBundle.isBundleEncoder) {
          this.#setPipelineState(this.#encoder, scene);

          if (mdBatch) {
            mdBatch.updateMaterials(this.#renderBatchUniforms);
            mdBatch.draw(this.#encoder, this.#mainPipeline);
            // Multidraw will process the whole RenderBatch => Enforce loop-end below
            endIndex = 0;
          } else {
            endIndex = rBatch.forEachWGPU(startIndex, this.#renderBatchUniforms.MAX_BATCH, callback);
          }
        }

        this.#finishRenderBundle(rBatch, 0, passEncoder, renderBundle);
        this.#encoder = passEncoder;

        // Inline ID-only draws: replay the same geometry into the ID pass encoder
        if (this.#wboitBatch && this.#idPassEncoder && this.#count) {
          this.#mainPipeline.reset(this.#bindGroupLayouts, rt.getIdTargets());
          this.#setPipelineState(this.#idPassEncoder, scene);

          for (let i = 0; i < this.#count;) {
            const geom = this.#geometriesList[i];
            const numInstances = geom.numInstances ?? 1;

            if (!geom.isLines) {
              this.#mainPipeline.drawOne(this.#idPassEncoder, this.#renderIndicesList[i], geom, idOnlyMaterialSettings, false, true);
            }
            i += numInstances;
          }
          // Restore targets for the next batch
          this.#mainPipeline.reset(this.#bindGroupLayouts, targets);
        }

        //Draw edge pass if required
        if (showEdges) {
          //Main pass edges
          if (this.#useRenderBundles && !renderBundleEdges) {
            renderBundleEdges = this.#createRenderBundle(renderBundleDescriptor);
          }

          if (renderBundleEdges && renderBundleEdges.isBundleEncoder) {
            this.#encoder = renderBundleEdges;
          } else {
            this.#encoder = passEncoder;
          }

          this.#edgePipeline.reset(this.#bindGroupLayouts, edgeTargets);

          if (!this.#useRenderBundles || renderBundleEdges.isBundleEncoder) {
            this.#setPipelineState(this.#encoder, scene);

            if (mdBatch) {
              mdBatch.draw(this.#encoder, this.#edgePipeline);
            } else {
              for (let i = 0; i < this.#count;) {
                const geom = this.#geometriesList[i];
                const numInstances = geom.numInstances ?? 1;
                this.#edgePipeline.drawOne(
                  this.#encoder, this.#renderIndicesList[i], geom, edgeMaterialSettings, this.#wboitBatch);

                // For instanced meshes, drawOne actually draws multiple instances at once, each requiring
                // one slot in the uniform batch. So we need to increment the index accordingly.
                i += numInstances;
              }
            }
          }

          this.#finishRenderBundle(rBatch, 1, passEncoder, renderBundleEdges);
          this.#encoder = passEncoder;
        }

        if (this.#count && needsFlush) {
          this.#flushObjects(this.#count);
        }

        startIndex = endIndex;

      } else {
        // Ghosted pass

        //This tricky bit plays along with the logic in RenderContext and RenderCommandSystem
        //Ghosting pass is edges only, but uses the override material.
        //Main pass edges use edgeMaterial, with color in its uniforms.
        //let overrideMaterial = scene.overrideMaterial;

        this.#renderBatchUniforms.setEdgeColorInt(this.#edgeColorGhostedInt);

        //Ghosted pass (currently draws edges and lines in a "ghosted/stippled" effect
        if (this.#useRenderBundles && !renderBundleGhosted) {
          renderBundleGhosted = this.#createRenderBundle(this.#renderBundleDescriptor);
        }

        if (renderBundleGhosted && renderBundleGhosted.isBundleEncoder) {
          this.#encoder = renderBundleGhosted;
        } else {
          this.#encoder = this.#passEncoder;
        }

        edgeTargets = renderToOverlay ? rt.getOverlayTargetsList() : rt.getTargetsListEdgePass();
        this.#edgePipeline.reset(this.#bindGroupLayouts, edgeTargets);

        this.#count = 0;

        let endIndex = 0;
        if (!this.#useRenderBundles || renderBundleGhosted.isBundleEncoder) {
          this.#setPipelineState(this.#encoder, scene);
          endIndex = rBatch.forEachWGPU(startIndex, this.#renderBatchUniforms.MAX_BATCH, callbackGhosted);
        }

        this.#finishRenderBundle(rBatch, MainPass.GHOSTED_BUNDLE_INDEX, this.#passEncoder, renderBundleGhosted);
        this.#encoder = this.#passEncoder;

        if (this.#count && needsFlush) {
          this.#flushObjects(this.#count);
        }
        startIndex = endIndex;
      }

    } while (startIndex > 0
    // TEMPORARY HACK: With render bundles the next iteration would use an already finished bundle encoder.
    //                 The code needs to be reworked to handle things properly.
    && !this.#useRenderBundles);

    // TEMPORARY HACK: This can only be true when using render bundles, see condition above. The code needs some work.
    if (startIndex > 0) {
      console.error("Tried to record RenderBundle for RenderBatch with more than MAX_BATCH fragments. This is not handled properly yet, some fragments won't be rendered.");
    }

    this.#encoder = null;

    this.#onBatchRecorded();

    // Clear depth buffer if requested - used for scenes that need to write to depth
    // but not interfere with the main models pass that happens after it.
    if (scene.clearDepthBuffer) {
      this.#clearTarget(this.#clearDepthDescriptor);
    }
  }

  #renderScenePart2D(scene, overrideMaterial, renderToOverlay = false) {

    let rt = this.#renderer.getRenderTargets();
    // Rendering to overlay doesn't affect the id targets.
    if (!renderToOverlay) {
      rt.setIdTargetsDirty();
    }

    let rBatch = scene;

    let targets = renderToOverlay ? rt.getOverlayTargetsList() : rt.getTargetsListMainPass();

    this.#renderBatchUniforms.setDoNotCutOverride(false);

    let startIndex = 0;
    do {
      let count = 0;
      if (!this.#commandEncoder) {
        this.#commandEncoder = this.#device.createCommandEncoder(
          { label: 'renderScenePart2D encoder' });
      }

      const passDescriptor = renderToOverlay ? this.#overlayPassDescriptorNoClear : this.#mainPassDescriptorProg;

      // The targets setup is different for 2D in overlay passes. Therefore, we need a separate pipeline for this case,
      // because the target setup is assumed to be constant by LinePipeline.
      const linePipeline = renderToOverlay ? this.#linePipelineOverlays : this.#linePipeline;
      linePipeline.reset(targets);

      //Main forward pass

      // false-positive issue detected by Chorus, see https://semgrep.dev/docs/ignoring-files-folders-code
      // nosemgrep
      this.#beginRenderPass2D('2D main forward pass', passDescriptor, linePipeline);

      let endIndex = rBatch.forEachWGPU(startIndex, this.#renderBatchUniforms.MAX_BATCH, (mesh) => {
        const material = overrideMaterial || mesh.material;

        // Note: We're not calling Renderer.initGeometry() here because:
        // 1. The lineSS shader can only handle geometry directly
        //    generated by VertexBufferBuilder. There is no way to use
        //    fallback geometry, and no need to interleave buffers.
        // 2. 2d render batches are not currently managed by
        //    OutOfCoreTileManager. They are all uploaded at draw time
        //    within VertexBuffer.js if necessary.
        // 3. At the moment we've decided that 2d geometry should ignore
        //    the write limit inside VertexBuffer due to it's typically
        //    small size.
        const geometry = mesh.geometry;

        const animDur = this.#renderer.getLoadingAnimationDuration();
        if (animDur > 0) {
          material.defines['LOADING_ANIMATION'] = true;
          if (geometry.creationTime) {
            mesh.meshAnimTime = Math.min(
              (this.#renderer.getTimestamp() - geometry.creationTime) / animDur,
              1.0);
          }
        }

        linePipeline.drawOne(this.#passEncoder, count, geometry, material);

        this.#renderBatchUniforms.setOneObjectData(mesh, count);
        this.#renderBatchUniforms.setOneMaterialData2D(material, 0);

        material.needsUpdate = false;

        count++;
      });

      this.#flushObjects(count);
      startIndex = endIndex;

    } while (startIndex > 0);

    this.#onBatchRecorded();
  }

  renderOverlay(scene, camera, materialPre, materialPost, showEdges, customEdgeColor, lights) {

    // Make sure the right camera is applied. An overlay may use an own camera (e.g. screen-aligned ortho).
    // In theory, we could skip this call if camera is the same as the scene camera,
    // but it's safer to always make sure that we get the latest state of the camera values.
    this.applyCamera(camera);

    if (this.#iblUniforms.update(lights)) {
      this.#frameBindGroup.updateBindGroup();
    }

    //NOTE: This logic renders the top side of the highlighted objects first,
    //and then the bottom side. The reason is that the top side material is opaque,
    //while we want to render the hidden parts of the object with faint transparency.
    //For objects that covers themselves and are also covered by other objects
    //this is a problem, since the opaque parts would prevent the back parts from showing.

    //However, edge rendering uses painter's algorithm settings for the depth,
    //since we don't care to show hidden edges from under top edges.

    // Note: We assume that this is only called for three scenes, i.e. actual overlays,
    // and NOT for RenderBatches that are part of an actual model.
    this.#rBatchShim.setFromScene(scene, this.#cameraUniforms.getViewProjectionMatrix());

    if (this.#rBatchShim.is2d()) {
      this.#renderScenePart2D(this.#rBatchShim, materialPre, true);
      return;
    }

    let rt = this.#renderer.getRenderTargets();
    let targets = rt.getOverlayTargetsList();

    this.#renderBatchUniforms.setDoNotCutOverride(!this.#rBatchShim.needsCutPlanes());

    let startIndex = 0,endIndex;
    do {
      let count = 0;
      if (!this.#commandEncoder) {
        this.#commandEncoder = this.#device.createCommandEncoder(
          { label: 'renderOverlay encoder' });
      }

      //Render top side of the object using the primary highlight material
      //or the overlay object's own material
      let overrideMaterial;
      if (materialPre) {
        overrideMaterial = materialPre;
      }

      this.#renderBatchUniforms.setEdgeColorInt(vectorToABGR(customEdgeColor || EdgeColor.HighlightUnder));

      this.#ensureRenderPass(
        // false-positive issue detected by Chorus, see https://semgrep.dev/docs/ignoring-files-folders-code
        // nosemgrep
        'overlay pass', this.#overlayPassDescriptorNoClear);
      this.#setPipelineState(this.#passEncoder, undefined);

      this.#overlayPipeline.reset(this.#bindGroupLayouts, targets);

      endIndex = this.#rBatchShim.forEachWGPU(startIndex, this.#renderBatchUniforms.MAX_BATCH, (mesh) => {
        const objMaterial = mesh.material;
        const geometry = this.#renderer.initGeometry(mesh.geometry);
        if (!geometry) {
          return true;
        }

        this.#geometriesList[count] = geometry;

        const material = overrideMaterial || objMaterial;

        const isInstanced = geometry.numInstances !== undefined;
        const numInstances = geometry.numInstances ?? 1;

        //It looks like we set the uniforms after the draw call here, but remember
        //that all these calls are just queuing commands that get issued when we
        //flush the command encoder, nothing is actually getting drawn yet.
        const materialTextureMask = this.#overlayPipeline.drawOne(this.#passEncoder, count, geometry, material, false);

        //The uniforms set here are also used for the edges pass below
        mesh.material = material; // make sure to set the correct material reference

        if (isInstanced) {
          // For instanced meshes, drawOne above actually draws multiple instances at once, each requiring
          // one slot in the uniform batch. So we need to increment the index accordingly.
          this.#renderBatchUniforms.setObjectDataFromInstanceBuffer(mesh, count);
          count += numInstances;
        } else {
          this.#renderBatchUniforms.setOneObjectData(mesh, count);
          count++;
        }

        this.#renderBatchUniforms.setOneMaterialData(material, materialTextureMask);

        // If material.needsUpdate was true, pipeline.drawOne() will always do the required updates.
        // So, we can (and must) safely reset it to false here.
        material.needsUpdate = false;
      });

      if (materialPost) {

        if (showEdges) {
          this.#edgePipeline.reset(this.#bindGroupLayouts, targets);
          for (let i = 0; i < count; i++) {
            this.#edgePipeline.drawOne(this.#passEncoder, i, this.#geometriesList[i], edgeHighlightMaterialUnder, false);
          }
        }

        //We need to flush the rendering pipe here, because the material settings are encoded
        //into the per-object uniforms (even though the same material is used for all objects in the scene)
        //This can be optimized by using two set of object uniforms buffers, or by using a pipeline
        //other than the uber shader pipeline, that can draw all objects with a fixed material
        this.#flushObjects(count);
        this.#commandEncoder = this.#device.createCommandEncoder(
          { label: 'renderOverlay 2nd encoder' });

        //Render bottom side of the object
        //for selection that's done using light transparency to show
        //areas the object spans under other objects
        {
          materialPost.depthFunc = 'greater';
          materialPost.needsUpdate = true;
          this.#renderBatchUniforms.setEdgeColorInt(vectorToABGR(customEdgeColor || EdgeColor.Highlight));

          this.#ensureRenderPass(
            'post pass (occluded)', this.#overlayPassDescriptorNoClear);
          this.#setPipelineState(this.#passEncoder, undefined);

          this.#overlayPipeline.reset(this.#bindGroupLayouts, targets);

          let count3 = 0;
          const material = materialPost;

          // TODO: This can be optimized away. The list of geometries to render has already been recorded
          // above. All we really need to do here is iterate over all indices and set the material reference.
          endIndex = this.#rBatchShim.forEachWGPU(startIndex, this.#renderBatchUniforms.MAX_BATCH, (mesh) => {
            const geometry = this.#renderer.initGeometry(mesh.geometry);
            if (!geometry) {
              return true;
            }

            const materialTextureMask = this.#overlayPipeline.drawOne(
              this.#passEncoder, count3, geometry, material, false);

            //The uniforms set here are also used for the edges pass below
            this.#renderBatchUniforms.setMaterialReference(count3 * this.#renderBatchUniforms.OBJECT_STRIDE_32, material);
            this.#renderBatchUniforms.setOneMaterialData(material, materialTextureMask);

            // If material.needsUpdate was true, pipeline.drawOne() will always do the required updates.
            // So, we can (and must) safely reset it to false here.
            material.needsUpdate = false;
            count3++;
          });

        }

      }

      //Finally render top side edges last
      if (showEdges) {
        this.#ensureRenderPass(
          'post edge pass (non-occluded)', this.#overlayPassDescriptorNoClear);
        this.#setPipelineState(this.#passEncoder, undefined);

        this.#edgePipeline.reset(this.#bindGroupLayouts, targets);
        for (let i = 0; i < count; i++) {
          this.#edgePipeline.drawOne(this.#passEncoder, i, this.#geometriesList[i], edgeHighlightMaterialOver, false);
        }
      }

      this.#flushObjects(count);

      startIndex = endIndex;

    } while (startIndex > 0);

    this.#onBatchRecorded();
  }

  getRenderBundle(renderBatch, index) {
    const bundles = this.#renderBundles.get(renderBatch);
    return bundles?.[index];
  }

  setRenderBundle(renderBatch, index, renderBundle) {
    let bundles = this.#renderBundles.get(renderBatch);
    if (!bundles) {
      bundles = [];
      this.#renderBundles.set(renderBatch, bundles);
    }
    bundles[index] = renderBundle;
  }

  clearRenderBundles(renderBatch) {
    this.#renderBundles.delete(renderBatch);
  }

  /** @returns {CameraUniforms} */
  getCameraUniforms() {
    return this.#cameraUniforms;
  }

  /** @param {number} value */
  setMaxBatchesPerSubmit(value) {
    this.#maxBatchesPerSubmit = value;
  }
}